import { PlainVersionedObject } from '../base/versioned-object';
import { Net } from '../data/net';
import { Strings } from '../generated/strings';
import { EnvironmentConnectionTypeInfo, EnvironmentModule } from '../manifest/environment-modules';

/**
 * The connection properties class.
 */
export interface ConnectionProperties extends MsftSme.StringMap<any> {
}

/**
 * The reserved connection property name on server.
 */
export class ConnectionPropertiesName {
    public static readonly PropertyDisplayName = 'displayName';
    public static readonly PropertyName = 'name';
    public static readonly PropertyNetworkName = 'networkName';
    public static readonly DisplayNameLocalhost = 'localhost';

    /**
     * Check if the display name is localhost.
     *
     * @param properties the connection properties.
     * @returns true if it's localhost.
     */
    public static isDisplayNameLocalhost(properties: ConnectionProperties): boolean {
        return !!properties && properties[ConnectionPropertiesName.PropertyDisplayName] === ConnectionPropertiesName.DisplayNameLocalhost;
    }

    /**
     * Gets the display name.
     *
     * @param properties the connection properties.
     * @returns the display name property value.
     */
    public static getDisplayName(properties: ConnectionProperties): string {
        return properties && properties[ConnectionPropertiesName.PropertyDisplayName];
    }

    /**
     * Gets the name.
     *
     * @param properties the connection properties.
     * @returns the name property value.
     */
    public static getName(properties: ConnectionProperties): string {
        return properties && properties[ConnectionPropertiesName.PropertyName];
    }

    /**
     * Gets the network name.
     *
     * @param properties the connection properties.
     * @returns the network name property value.
     */
    public static getNetworkName(properties: ConnectionProperties): string {
        return properties && properties[ConnectionPropertiesName.PropertyNetworkName];
    }
}

/**
 * The connection attributes class.
 */
export interface ConnectionAttribute {

    /**
     * The id string of this attribute
     */
    id: string;

    /**
     * The value of the attribute. used for attributes that can have variable values such as Operating System
     */
    value?: string | number;
}

/**
 * The connection class.
 */
export interface Connection {

    /**
     * The id of the connection, this is unique per connection
     */
    id: string;

    /**
     * The type of connection
     */
    type: string;

    /**
     * The name of the connection, this is unique per connection type
     */
    name: string;

    /**
     * The fallback connection aliases list
     */
    aliases?: string[];

    /**
     * The active alias, last known good name, null or undefined if not in use
     */
    activeAlias?: string;

    /**
     * The property bag of the connection
     */
    properties?: ConnectionProperties;

    /**
     * The ids of attributes identified for this connection
     */
    attributes?: ConnectionAttribute[];

    /**
     * The tags the user(s) have assigned to this connection
     */
    tags?: string[];

    /**
     * The groupId of the connection
     */
    groupId?: string;

    /**
     * The settings the users/extensions have assigned to this connection
     */
    settings?: PlainVersionedObject;

    /**
     * Display type name
     */
    displayTypeName?: string;
}

/**
 * Defines connection type strings known by core
 * Be careful that these strings match what is defined by the manifest of @msft-sme/server-manager
 */
export const connectionTypeConstants = {
    server: 'msft.sme.connection-type.server',
    cluster: 'msft.sme.connection-type.cluster',
    windowsClient: 'msft.sme.connection-type.windows-client',
    eflowDevice: 'microsoft.connection-type.eflow-device',
    clusterNodesProperty: 'nodes'
};

/**
 * Connection Utility class.
 */
export class ConnectionUtility {

    /**
     * Determines if one connection is referring to the same object as another connection
     *
     * @param a the first connection in the comparison
     * @param b the second connection in the comparison
     * @returns true if the connections are of the same type and have the same name
     */
    public static areEqual(a: Connection, b: Connection): boolean {
        if (!a || !b) {
            return a === b;
        }

        if (a.type !== b.type) {
            return false;
        }

        if (a.name !== b.name) {
            return false;
        }

        if (a.id !== b.id) {
            return false;
        }

        return true;
    }

    /**
     * Determines if the given connection is to a server
     *
     * @param connection the connection to check
     */
    public static isServer(connection: Connection) {
        return connection.type === connectionTypeConstants.server;
    }

    /**
     * Determines if the given connection is to a cluster connection.
     * Currently we support: HCI and Failover Cluster.
     *
     * @param connection the connection to check
     */
    public static isCluster(connection: Connection): boolean {
        return connection.type === connectionTypeConstants.cluster;
    }

    /**
     * Determines if the given connection is to a FailOver cluster
     *
     * @param connection the connection to check
     */
    public static isFailoverCluster(connection: Connection) {
        return !MsftSme.isNullOrUndefined(connection.properties) && connection.properties['connectionType'] === 'cluster'
            || (MsftSme.isNullOrUndefined(connection.properties['connectionType']) &&
                connection.type === connectionTypeConstants.cluster);
    }

    /**
     * Determines if the given connection is to a windows client
     *
     * @param connection the connection to check
     */
    public static isWindowsClient(connection: Connection) {
        return connection.type === connectionTypeConstants.windowsClient;
    }

    /**
     * Determines if the given connection is to an EFLOW device
     *
     * @param connection the connection to check
     */
    public static isEflowDevice(connection: Connection) {
        return connection.type === connectionTypeConstants.eflowDevice;
    }

    /**
     * Determines if the given connection is to a node
     *
     * @param connection the connection to check
     */
    public static isNode(connection: Connection) {
        return this.isServer(connection) || this.isCluster(connection) || this.isWindowsClient(connection)
          || this.isEflowDevice(connection);
    }

    /**
     * Gets the name of a node from a connection. This assumes the connection is to a single server or cluster.
     *
     * @param connection the connection object. (should be of type server or cluster)
     * @param throwError throw an error if not a server or cluster.
     */
    public static getNodeName(connection: Connection, throwError: boolean = false) {
        if (!this.isNode(connection)) {
            if (throwError) {
                const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ExpectedSingleNode.message;
                throw new Error(message);
            }

            return null;
        }

        return connection.name;
    }

    /**
     * Gets the name of a valid node from a connection. This assumes the connection is to a single server or cluster.
     *   if activeAlias is used return it, otherwise return connection.name
     *
     * @param connection the connection object. (should be of type server or cluster)
     * @param throwError throw an error if not a server or cluster.
     */
    public static getValidNodeName(connection: Connection, throwError: boolean = false): string {
        if (!this.isNode(connection)) {
            if (throwError) {
                const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ExpectedSingleNode.message;
                throw new Error(message);
            }

            return null;
        }
        if (connection.activeAlias) {
            return connection.activeAlias;
        } else {
            return connection.name;
        }
    }

    /**
     * Gets nodes of a connection of type cluster
     *
     * @param connection the connection object. (should be of type cluster)
     * @param throwError throw an error if not a cluster.
     */
    public static getClusterNodes(connection: Connection, throwError: boolean = false): string[] {
        if (!this.isCluster(connection)) {
            if (throwError) {
                const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ExpectedClusterNode.message;
                throw new Error(message);
            }

            return [];
        }

        return connection.properties[connectionTypeConstants.clusterNodesProperty];
    }

    /**
     * Gets the connection type info for a given connection
     *
     * @param connection the connection object.
     */
    public static getConnectionTypeInfo(connection: Connection): EnvironmentConnectionTypeInfo {
        return EnvironmentModule.getConnectionTypeInfo(connection.type);
    }

    /**
     * creates a connection Identifier
     *
     * @param connectionType the connection type.
     * @param connectionName the connection name.
     */
    public static createConnectionId(connectionType: string, connectionName: string, groupId?: string): string {
        let id = `${connectionType.toLocaleLowerCase()}!${connectionName.toLocaleLowerCase()}`;
        if (groupId) {
            id = id + `!${groupId.toLocaleLowerCase()}`;
        }
        return id;
    }

    /**
     * Ensures tat important fields in a connection are lowercase
     */
    public static forceLowercase(connection: Connection): void {
        connection.id = connection.id.toLocaleLowerCase();
        connection.name = connection.name.toLocaleLowerCase();
        connection.type = connection.type.toLocaleLowerCase();
    }
    /**
     * Ensures tat important fields in a connection are lowercase
     */
    public static hasTags(connection: Connection): boolean {
        return connection.tags && connection.tags.length > 0;
    }

    public static convertToConnectionName(inputName: string): string {
        return inputName.indexOf(':') < 0 ? inputName.toLowerCase() : Net.convertIPv6ToLiteral(inputName);
    }
}
